import axios, { AxiosTransformer, Method } from 'axios';
import toSnakeCase from 'snakecase-keys';
import toCamelCase from 'camelcase-keys';
import { isEmpty } from 'ramda';
import getCookie from './getCookie';
import { AuthToken } from '../context/authContext';
import env from './env';

// axios.defaults.withCredentials = true;

type ErrorCodes =
  | '5000-ERR'
  | '5010-ERR'
  | '5020-ERR'
  | '5030-ERR'
  | '5040-ERR'
  | '5050-ERR'
  | '5060-ERR'
  | '5070-ERR'
  | '5080-ERR';

function getErrorMessage(code: ErrorCodes, fndError: any) {
  switch (code) {
    case '5000-ERR':
      return fndError?.description;
    case '5010-ERR':
      return fndError?.detail?.[0]?.message;
    case '5080-ERR':
      return fndError?.description;
    case '5030-ERR':
      return fndError?.description;
    default:
      return 'Oops. Something unusual happened at our end. Please contact our support team!';
  }
}

const objectToQueryString = (obj: Record<string, unknown>) => {
  const data = obj && toSnakeCase(obj, { deep: true });
  const params = new URLSearchParams(data);
  return params.toString();
};

export const transformRequest = [
  (data: Record<string, unknown>) => {
    return data && toSnakeCase(data, { deep: true });
  },
  ...(axios.defaults.transformRequest as AxiosTransformer[]),
];

export const transformResponse = [
  ...(axios.defaults.transformResponse as AxiosTransformer[]),
  (data: Record<string, unknown>) => {
    return data && toCamelCase(data, { deep: true });
  },
];

const defaultRequests = {
  baseURL: env('POS_API_URL'),
  headers: () => {
    let AUTH_TOKEN = null;

    if (getCookie('_pos_tokid')) {
      const authToken = JSON.parse(getCookie('_pos_tokid')) as AuthToken;
      AUTH_TOKEN = authToken.token;
    }

    return {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(AUTH_TOKEN ? { Authorization: `Bearer ${AUTH_TOKEN}` } : {}),
    };
  },
  transformRequest,
  transformResponse,
};

export const formatErrors = (error: any) => {
  let phoneNumber = '';
  let userId = '';

  const authTokenCookie = getCookie('_pos_tokid');

  if (authTokenCookie) {
    phoneNumber = JSON.parse(authTokenCookie)?.phoneNumber || '';
    userId = JSON.parse(authTokenCookie)?.userId || '';
  }

  const fndError = error?.response?.data?.error;

  if (fndError) {
    const status = error?.response?.status;
    const message = getErrorMessage(fndError.code, fndError);

    const config = {
      url: error?.response?.config?.url || '',
      method: error?.response?.config?.method || '',
      data: fndError,
      phoneNumber,
      userId,
    };

    return { config, status, message };
  }

  const config = {
    url: '',
    method: '',
    data: '',
    phoneNumber,
    userId,
  };

  const status = 'FRONTEND ERROR';

  const message = error?.message;

  return { config, status, message };
};

export default function request(defaultConfigs = {}) {
  const defaults = { ...defaultRequests, ...defaultConfigs };
  const api = (method: Method, url: string, data: any) => {
    return new Promise((resolve, reject) => {
      axios({
        url: `${defaults.baseURL}${url}`,
        method,
        headers: defaults.headers(),
        params: method === 'get' ? data : undefined,
        data: method !== 'get' ? data : undefined,
        paramsSerializer: objectToQueryString,
        transformRequest: defaults.transformRequest,
        transformResponse: defaults.transformResponse,
      }).then(
        response => {
          return resolve(response?.data?.data);
        },
        error => {
          reject(error);
        },
      );
    });
  };

  return {
    get: (url: string, data?: any) => api('get', url, data),
    post: (url: string, data?: any) => api('post', url, data),
    patch: (url: string, data?: any) => api('patch', url, data),
    put: (url: string, data?: any) => api('put', url, data),
    delete: (url: string, data?: any) => api('delete', url, data),
  };
}
